import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useLang } from '@/contexts/LanguageContext';
import api from '@/utils/api';
import ScrollReveal from '@/components/effects/ScrollReveal';
import { MessageSquare, ArrowRight } from 'lucide-react';

function CategorySkeleton() {
  return (
    <div className="glass-card rounded-2xl p-6 flex items-center gap-6 animate-pulse">
      <div className="w-10 h-10 rounded-full bg-sacred-gold/10 shrink-0" />
      <div className="flex-1 min-w-0 space-y-2">
        <div className="h-4 bg-sacred-gold/10 rounded w-1/3" />
        <div className="h-3 bg-sacred-gold/5 rounded w-2/3" />
      </div>
      <div className="hidden sm:flex items-center gap-6 shrink-0">
        <div className="text-center space-y-1">
          <div className="h-4 w-6 bg-sacred-gold/10 rounded mx-auto" />
          <div className="h-3 w-10 bg-sacred-gold/5 rounded" />
        </div>
        <div className="w-4 h-4 bg-sacred-gold/5 rounded" />
      </div>
    </div>
  );
}

export default function Forum() {
  const { t, localizedField } = useLang();
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get('/forum/categories').then(r => { setCategories(r.data); setLoading(false); }).catch(() => setLoading(false));
  }, []);

  return (
    <div data-testid="forum-page" className="min-h-screen py-20 md:py-32 px-4">
      <div className="max-w-4xl mx-auto">
        <ScrollReveal>
          <h1 className="text-4xl sm:text-5xl font-display text-heading mb-3" data-testid="forum-title">{t('forum.title')}</h1>
          <p className="text-text-muted font-body mb-12">A community of thinkers, builders, and seekers.</p>
        </ScrollReveal>

        <div className="space-y-4">
          {loading ? (
            <>
              <CategorySkeleton />
              <CategorySkeleton />
              <CategorySkeleton />
              <CategorySkeleton />
            </>
          ) : categories.length === 0 ? (
            <div className="glass-card rounded-2xl p-8 text-center">
              <MessageSquare size={40} className="text-sacred-gold/20 mx-auto mb-3" />
              <p className="text-text-muted font-body">{t('common.noResults')}</p>
            </div>
          ) : (
            categories.map((cat, i) => (
              <ScrollReveal key={cat.id} delay={i * 0.08}>
                <Link to={`/forum/${cat.slug}`} className="glass-card rounded-2xl p-6 flex items-center gap-6 group" data-testid={`forum-cat-${cat.slug}`}>
                  <div className="text-3xl shrink-0">{cat.icon}</div>
                  <div className="flex-1 min-w-0">
                    <h3 className="text-lg font-display text-heading group-hover:text-sacred-gold transition-colors">{localizedField(cat.name)}</h3>
                    <p className="text-text-muted/70 text-sm font-body">{localizedField(cat.description)}</p>
                  </div>
                  <div className="hidden sm:flex items-center gap-6 text-text-muted/50 text-sm font-body shrink-0">
                    <div className="text-center">
                      <p className="text-warm-brown font-semibold">{cat.thread_count || 0}</p>
                      <p className="text-xs">{t('forum.threads')}</p>
                    </div>
                    <ArrowRight size={18} className="text-sacred-gold/50 group-hover:text-sacred-gold transition-colors" />
                  </div>
                </Link>
              </ScrollReveal>
            ))
          )}
        </div>
      </div>
    </div>
  );
}
